import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { History, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckInOutHistory = () => {
  const { getAuthHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/checkinout-logs?limit=100`, { headers: getAuthHeaders() });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch history');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Check In/Out History</h1>
        <p className="text-muted-foreground mt-1">Track all equipment movements</p>
      </div>

      {/* History Table */}
      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-center gap-2">
          <History className="w-5 h-5 text-muted-foreground" />
          <CardTitle className="text-lg">Activity Log ({logs.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Date & Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                    No check in/out activity yet.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} className="table-row-hover" data-testid={`log-row-${log.id}`}>
                    <TableCell>
                      {log.action === 'check_out' ? (
                        <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">
                          <ArrowUpRight className="w-3 h-3 mr-1" />
                          Checked Out
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                          <ArrowDownRight className="w-3 h-3 mr-1" />
                          Checked In
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{log.item_name}</TableCell>
                    <TableCell>{log.user_name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {log.notes || '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckInOutHistory;
